import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Intestazione } from '../../componenti/Pezzi.tsx'
import { Bottone } from '../../componenti/base.tsx'
import { messaggioErrore } from '../../componenti/elenco.tsx'
import {
  BarraCalendario, Calendario, periodoDellaVista, type EventoCalendario, type TonoEvento, type Vista,
} from '../../componenti/calendario/Calendario.tsx'
import { oggiARoma, periodoInParole } from '../../componenti/calendario/date.ts'
import type { StatoMediazione } from '../../servizi/mediazioni.ts'
import { ModuloIndisponibilita } from '../agenda/comune.tsx'

/**
 * «Il mio calendario» del mediatore (557; docs/15-calendari-sedi.md, fase 2): le sue mediazioni, le stesse
 * di «Le mie mediazioni» (`mediazione.propria`), come giorno, settimana, mese o elenco. Nella griglia niente
 * nomi: si apre la mediazione per leggerli, come dall'elenco. Il file .ics porta tutte le sue mediazioni
 * nel calendario del telefono o di Outlook, senza nomi.
 *
 * «Segnati non disponibile» (559, decisione 5): il mediatore che non ha l'agenda della sede si segna non
 * disponibile da qui, senza motivo; sotto il calendario le sue indisponibilita' da oggi in poi, con «Togli».
 * Chi programma le mediazioni riceve l'avviso «non disponibile» e lo vede in arancione.
 */

type Indisponibilita = { id: string; dal: string; al: string; dalle: string | null; alle: string | null }

type MiaVoce = {
  id: string; giorno: string; dalle: string; alle: string; stato: StatoMediazione
  lingua_nome: string | null; sede_nome: string | null; fuori_sede: boolean
  conflitti: { messaggio: string; gravita: 'rosso' | 'arancione' }[]
}

const VISTE: Vista[] = ['giorno', 'settimana', 'mese', 'elenco']

const tonoDi = (m: MiaVoce): TonoEvento => (m.stato === 'annullata' ? 'annullato'
  : m.conflitti.length ? 'rosso' : m.stato === 'programmata' ? 'normale' : 'concluso')

export function IlMioCalendario() {
  const naviga = useNavigate()
  const [parametri, setParametri] = useSearchParams()
  const vista = (VISTE as string[]).includes(parametri.get('vista') ?? '') ? parametri.get('vista') as Vista : 'settimana'
  const giorno = /^\d{4}-\d{2}-\d{2}$/.test(parametri.get('giorno') ?? '') ? parametri.get('giorno')! : oggiARoma()
  const [mie, setMie] = useState<MiaVoce[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [assenze, setAssenze] = useState<Indisponibilita[]>([])
  const [assenza, setAssenza] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const caricaAssenze = () => api.get<Indisponibilita[]>('/agenda/indisponibilita').then(setAssenze).catch(() => setAssenze([]))
  useEffect(() => { void caricaAssenze() }, [])

  async function togli(a: Indisponibilita) {
    setErrore(null); setEsito(null); setInCorso(a.id)
    try {
      await api.post(`/agenda/eventi/${a.id}/annulla`)
      setEsito('Tolto: in quei giorni risulti di nuovo disponibile.')
      await caricaAssenze()
    } catch (e) { setErrore(messaggioErrore(e)) } finally { setInCorso(null) }
  }

  const cambia = (valori: Record<string, string>) => {
    const nuovi = new URLSearchParams(parametri)
    for (const [k, v] of Object.entries(valori)) nuovi.set(k, v)
    setParametri(nuovi, { replace: true })
  }

  const { dal, al } = periodoDellaVista(vista, giorno)
  useEffect(() => {
    let vivo = true
    setErrore(null)
    api.get<{ mediazioni: MiaVoce[] }>(`/mediazioni/mie/calendario?dal=${dal}&al=${al}`)
      .then((r) => { if (vivo) setMie(r.mediazioni) })
      .catch((e) => { if (vivo) setErrore(e instanceof ErroreApi ? e.message : 'Non riesco a caricare il tuo calendario.') })
    return () => { vivo = false }
  }, [dal, al])

  const eventi: EventoCalendario[] = useMemo(() => (mie ?? []).map((m) => ({
    chiave: m.id, giorno: m.giorno, dalle: m.dalle, alle: m.alle,
    titolo: m.lingua_nome ? `Mediazione in ${m.lingua_nome.toLowerCase()}` : 'Mediazione',
    dettaglio: m.fuori_sede ? 'fuori sede' : m.sede_nome ?? undefined,
    tono: tonoDi(m), avvisi: m.conflitti.map((c) => c.messaggio),
    apri: () => naviga(`/mediazioni/${m.id}`),
  })), [mie, naviga])

  return (
    <>
      <Intestazione titolo="Il mio calendario"
                    sottotitolo={<>Le mediazioni che ti hanno assegnato. Aprine una per vedere la persona e il luogo; l’elenco è in <Link to="/mediazioni">Le mie mediazioni</Link>.</>}
                    azioni={<Bottone variante="secondario" dimensione="piccolo" onClick={() => { setEsito(null); setAssenza(true) }}>
                      Segnati non disponibile
                    </Bottone>} />
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      <BarraCalendario vista={vista} cambiaVista={(v) => cambia({ vista: v })} giorno={giorno}
                       cambiaGiorno={(g) => cambia({ giorno: g })} viste={VISTE}>
        <a className="bottone bottone-secondario bottone-piccolo" href="/api/mediazioni/mie.ics" download
           title="Le tue mediazioni da un mese fa a sei mesi avanti, senza nomi">
          Scarica il file per il calendario (.ics)
        </a>
      </BarraCalendario>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!mie && !errore && <Attesa testo="Caricamento del calendario…" />}
      {mie && (
        <Calendario vista={vista} giorno={giorno} eventi={eventi} vuoto="Nessuna mediazione."
                    apriGiorno={(g) => cambia({ vista: 'giorno', giorno: g })} />
      )}
      {mie && (
        <p className="tenue piccolo">
          Il file .ics aggiunge le tue mediazioni al calendario del telefono o di Outlook: dentro ci sono giorno, ora,
          lingua e sede, mai il nome della persona. Se una mediazione cambia, scaricalo di nuovo: il calendario la aggiorna.
        </p>
      )}
      {assenze.length > 0 && (
        <>
          <h3 className="rete-titoletto">Quando non sei disponibile</h3>
          <ul className="telefoni-connessi">
            {assenze.map((a) => (
              <li key={a.id}>
                <span>{periodoInParole(a.dal, a.al)}{a.dalle ? `, dalle ${a.dalle} alle ${a.alle}` : ', tutto il giorno'}</span>
                <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === a.id} onClick={() => void togli(a)}>Togli</Bottone>
              </li>
            ))}
          </ul>
        </>
      )}
      {assenza && (
        <ModuloIndisponibilita chiudi={() => setAssenza(false)}
                               fatto={(testo) => { setAssenza(false); setEsito(testo); void caricaAssenze() }} />
      )}
    </>
  )
}
